import React, { useEffect, useState } from "react";
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ApprovalTypeTag from "@/components/tags/ApprovalTypeTag";
import { ApprovalType, LeaveRequestDTO } from "@/models/leave-requests.model";
import { Button } from "../ui/button";
import UserSnippet from "../user/UserSnippet";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { formatDateLocale } from "@/utils/date-format";
import { Detail } from "@/admin/dashboard/components/approval-dialog/ApprovalDialog";
import { Separator } from "../ui/separator";
import { getSignedUrl } from "@/api/leave-requests";
import { IoOpenOutline } from "react-icons/io5";

interface Props {
  requestObj: LeaveRequestDTO;
  handleDelete: () => void;
  isOpen: boolean;
}

function ViewOnlyLeaveRequest({ requestObj, handleDelete, isOpen }: Props) {
  const [request, setRequest] = useState<LeaveRequestDTO>(requestObj);
  const [imageSignedUrl, setImageSignedUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState<boolean>(false);

  useEffect(() => {
    async function fetchSignedUrl() {
      if (!requestObj.fileUrl) return;
      console.log("Fetching signed URL for file:", requestObj.fileUrl);
      const { signedUrl } = await getSignedUrl(requestObj.fileUrl);
      setImageSignedUrl(signedUrl);
      const fileExtension = requestObj.fileUrl?.split('.').pop()?.toLowerCase();
      const isPdf = fileExtension === 'pdf';
      setIsPdf(isPdf);

    }
    setRequest(requestObj);
    if (isOpen) {
      fetchSignedUrl();
    }

  }, [requestObj, isOpen]);

  return (
    <SheetContent className="sm:max-w-lg flex flex-col h-full">
      {/* Header Section */}
      <SheetHeader>
        <SheetTitle className="text-2xl font-semibold">
          Leave Request Details
        </SheetTitle>
        <SheetDescription>
          View details of your leave request.
          <br />
          {request.status === ApprovalType.PENDING
            ? "This request is pending approval from your admin."
            : ""}
        </SheetDescription>
      </SheetHeader>

      <Separator />

      {/* Main Content */}
      <div className="flex flex-col gap-6 flex-grow overflow-y-auto">
        <div className="font-bold text-lg">Your Leave Request</div>

        <div className="flex flex-wrap gap-10 items-start">
          <Detail title="Status">
            <ApprovalTypeTag status={request.status} />
          </Detail>
          <Detail title="Type">
            <span className="font-semibold">
              {capitalizeFirstLetter(request.type)}
            </span>
          </Detail>
          <Detail
            title={`${request.status === ApprovalType.PENDING
              ? "Requested"
              : request.status === ApprovalType.APPROVED
                ? "Approved"
                : request.status === ApprovalType.REJECTED
                  ? "Rejected"
                  : ""
              } On`}
          >
            <span className="font-semibold">
              {request.status === ApprovalType.PENDING
                ? formatDateLocale(request.createdAt)
                : formatDateLocale(request.updatedAt)}
            </span>
          </Detail>
        </div>

        <div className="flex flex-wrap gap-10 items-start">
          <Detail title="From Date">
            <span className="font-semibold">
              {formatDateLocale(request.fromDate)}{" "}
              {request.fromDateIsAm ? "(AM)" : "(PM)"}
            </span>
          </Detail>
          <Detail title="To Date">
            <span className="font-semibold">
              {formatDateLocale(request.toDate)}{" "}
              {request.toDateIsAm ? "(AM)" : "(PM)"}
            </span>
          </Detail>
          <Detail title="Duration">
            <span className="font-semibold">{request.days} days</span>
          </Detail>
        </div>

        <div>
          <Detail title="Reason">
            <div className="border border-gray-200 w-full p-4 rounded-md text-gray-600 min-h-[100px] max-h-[200px] overflow-y-auto">
              {request.reason ? (
                request.reason
              ) : (
                <div className="italic text-gray-400">No reason stated.</div>
              )}
            </div>
          </Detail>
        </div>

        <div>
          <Detail title="Attachment">
            {imageSignedUrl ?
              <div className="w-full h-full flex justify-center items-center group relative overflow-hidden rounded">
                {isPdf ?
                  <iframe
                    src={`${imageSignedUrl}#toolbar=0`}
                    title="PDF attachment"
                    className="w-full h-[300px] border rounded"
                  />
                  : <img
                    src={imageSignedUrl}
                    alt="Attached file"
                    className="max-w-full max-h-[300px] object-contain transition-transform duration-300 group-hover:scale-105"
                  />}


                <a
                  href={imageSignedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-2 right-2 bg-white text-black px-3 py-1 rounded shadow hover:bg-gray-100 transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <IoOpenOutline />
                </a>
              </div>


              : <div className="italic text-gray-400">No image uploaded.</div>}
          </Detail>
        </div>
      </div>


      {/* Footer Section */}
      {request.status === ApprovalType.PENDING && (
        <div className="flex flex-col gap-4 mt-auto sticky bottom-0 bg-white pb-4">
          <Separator />
          <SheetFooter className="w-full flex flex-col sm:flex-row sm:justify-between gap-4 mt-auto items-center">
            <p className="text-sm text-gray-500">
              Note that this action cannot be undone.
            </p>
            <Button variant="outline" onClick={handleDelete}>
              Delete
            </Button>
          </SheetFooter>
        </div>
      )}
    </SheetContent>
  );
}

export default ViewOnlyLeaveRequest;
